import type { ReactNode } from "react";
import { useState } from "react";
import { Link, useParams } from "react-router-dom";
import { useCrews, useJobs, useLocations, useTerritory } from "@/hooks/use-api";
import { DetailPage } from "@/components/v2/detail-page";
import { DetailPageActions } from "@/components/v2/detail-page-actions";
import { KPIRow } from "@/components/v2/kpi-row";
import {
  EntityActivityFeed,
  EntityActivityPreview,
} from "@/components/v2/entity-activity";
import { Tabs, TabsContent, TabsList, TabsTrigger } from "@/components/ui/tabs";
import { formatDate } from "@/lib/utils";

// Pattern C (perennial entity, ADV-782 / ADR-0019). Territories have no
// lifecycle/coverage window of their own — the page surfaces what's currently
// served (locations, active jobs, assigned crews) and a streaming activity
// feed. No PageHeader `tabs` prop yet — tabs mount inline directly under
// DetailPage, mirroring customers/detail.tsx; lifting `tabs` to PageHeader is
// a separate refactor when a second detail page wants it.

type Row = Record<string, unknown>;

const EM_DASH = "—";

function str(value: unknown): string | null {
  return typeof value === "string" && value.length > 0 ? value : null;
}

interface InfoRowProps {
  label: string;
  value: string | null;
  testId?: string;
}
function InfoRow({ label, value, testId }: InfoRowProps) {
  return (
    <div
      className="flex items-baseline justify-between gap-2 py-1"
      data-testid={testId}
    >
      <span className="typo-label text-[color:var(--color-text-lo)] shrink-0">
        {label}
      </span>
      <span className="typo-body text-[color:var(--color-text-md)] text-right truncate">
        {value ?? EM_DASH}
      </span>
    </div>
  );
}

function RailSection({
  title,
  children,
}: {
  title: string;
  children: ReactNode;
}) {
  return (
    <div>
      <div
        className="typo-label text-[color:var(--color-text-lo)] uppercase tracking-wide mb-2"
        data-testid="rail-title"
      >
        {title}
      </div>
      {children}
    </div>
  );
}

function SectionHeader({
  title,
  count,
}: {
  title: string;
  count: number | null;
}) {
  return (
    <div className="flex items-baseline justify-between mb-2">
      <h2 className="typo-heading-2 text-[color:var(--color-text-hi)]">
        {title}
      </h2>
      {count != null && (
        <span className="typo-label text-[color:var(--color-text-lo)]">
          {count}
        </span>
      )}
    </div>
  );
}

function LocationsSection({
  rows,
  total,
  isLoading,
}: {
  rows: Row[];
  total: number;
  isLoading: boolean;
}) {
  return (
    <section aria-labelledby="locations-section-heading">
      <SectionHeader title="Locations" count={isLoading ? null : total} />
      {!isLoading && rows.length === 0 && (
        <p className="typo-body text-[color:var(--color-text-lo)] py-3">
          No locations in this territory yet.
        </p>
      )}
      {rows.length > 0 && (
        <ul className="divide-y divide-[color:var(--color-border)] rounded-md border border-[color:var(--color-border)] bg-[color:var(--color-surface)]">
          {rows.map((loc) => {
            const id = str(loc.id) ?? "";
            const name =
              str(loc.formatted_address) ??
              str(loc.street_line_1) ??
              "Location";
            const city = str(loc.city);
            const state = str(loc.state);
            const locType = str(loc.location_type);
            return (
              <li key={id} className="px-4 py-2">
                <Link
                  to={`/locations/${id}`}
                  className="flex items-baseline justify-between gap-3 hover:bg-[color:var(--color-surface-hover)] -mx-4 px-4 py-1 rounded-sm"
                >
                  <span className="typo-body text-[color:var(--color-text-hi)] truncate">
                    {name}
                  </span>
                  <span className="typo-label text-[color:var(--color-text-lo)] shrink-0">
                    {[city, state].filter(Boolean).join(", ")}
                    {locType ? ` · ${locType}` : ""}
                  </span>
                </Link>
              </li>
            );
          })}
        </ul>
      )}
    </section>
  );
}

function CrewsSection({
  rows,
  total,
  isLoading,
}: {
  rows: Row[];
  total: number;
  isLoading: boolean;
}) {
  return (
    <section aria-labelledby="crews-section-heading">
      <SectionHeader title="Crews" count={isLoading ? null : total} />
      {!isLoading && rows.length === 0 && (
        <p className="typo-body text-[color:var(--color-text-lo)] py-3">
          No crews assigned to this territory.
        </p>
      )}
      {rows.length > 0 && (
        <ul className="divide-y divide-[color:var(--color-border)] rounded-md border border-[color:var(--color-border)] bg-[color:var(--color-surface)]">
          {rows.map((crew) => {
            const id = str(crew.id) ?? "";
            const name = str(crew.name) ?? "Crew";
            const status = str(crew.status);
            const workerCount =
              typeof crew.worker_count === "number" ? crew.worker_count : null;
            const startedOn = str(crew.started_on);
            // Crews detail page is ADV-785 (still Backlog). Render the name
            // as a non-anchor span with aria-disabled + tooltip so the row is
            // visibly present but doesn't 404. Becomes a 1-line swap when
            // /crews/:id ships.
            return (
              <li
                key={id}
                className="flex items-baseline justify-between gap-3 px-4 py-2"
              >
                <span
                  className="typo-body text-[color:var(--color-text-md)] truncate cursor-not-allowed"
                  aria-disabled="true"
                  title="Crew detail coming soon"
                >
                  {name}
                </span>
                <span className="typo-label text-[color:var(--color-text-lo)] shrink-0">
                  {status ?? EM_DASH}
                  {workerCount != null ? ` · ${workerCount} workers` : ""}
                  {startedOn ? ` · started ${formatDate(startedOn)}` : ""}
                </span>
              </li>
            );
          })}
        </ul>
      )}
    </section>
  );
}

export default function V2TerritoryDetailPage() {
  const { id } = useParams<{ id: string }>();
  const territoryId = id ?? "";

  const { data, isLoading, isError } = useTerritory(territoryId);
  const territory = (data as { territory?: Row } | undefined)?.territory;

  // Page-level hook calls. Lifted to the page so the KPI tiles and the
  // section rows + Coverage rail all read from one query each — TanStack
  // Query dedupes identical keys, so component-level reuse would also work,
  // but lifting keeps the data flow explicit and avoids passing a hook
  // identity down. limit:50 is enough for the Pattern C section list; if
  // a territory exceeds that, users navigate to the full list page.
  const locationsQuery = useLocations({
    territory_id: territoryId,
    limit: 50,
  });
  // "Active Jobs" KPI = jobs currently being worked. The jobs API validates
  // status against `jobStatuses` (new | scheduled | in_progress | completed
  // | canceled), so "active" is rejected by Hono — use the in_progress enum
  // value to match what the KPI label says.
  const activeJobsQuery = useJobs({
    territory_id: territoryId,
    status: "in_progress",
    limit: 50,
  });
  const crewsQuery = useCrews({ territory_id: territoryId, limit: 50 });

  const locations =
    (locationsQuery.data as { locations?: Row[] } | undefined)?.locations ?? [];
  const locationsTotal =
    (locationsQuery.data as { total?: number } | undefined)?.total ??
    locations.length;

  const crews = (crewsQuery.data as { crews?: Row[] } | undefined)?.crews ?? [];
  const crewsTotal =
    (crewsQuery.data as { total?: number } | undefined)?.total ?? crews.length;

  // Active jobs only contribute their count to the KPI + Coverage rail —
  // the page doesn't show a jobs list (the full Jobs view is the right
  // navigation for that, filtered by territory).
  const activeJobsTotal =
    (activeJobsQuery.data as { total?: number } | undefined)?.total ?? 0;

  const name = str(territory?.name) ?? "Territory";
  const region = str(territory?.region);

  const [tab, setTab] = useState<string>("overview");

  return (
    <DetailPage
      isLoading={isLoading}
      isError={isError}
      isNotFound={!isLoading && !isError && !territory}
      title={name}
      subtitle={region ?? undefined}
      breadcrumbs={[
        { label: "Territories", href: "/territories" },
        { label: name },
      ]}
      notFoundMessage={{
        title: "Territory not found",
        description: "This territory may have been deleted.",
      }}
      actions={<DetailPageActions entityLabel="Territory" />}
    >
      {/* Inline properties strip — status/code/owner are missing from
          core.territories (follow-up to extend the schema); render as "—". */}
      <div className="flex items-center gap-1.5 flex-wrap typo-body text-[color:var(--color-text-md)] mt-1">
        <span
          className="inline-flex items-center rounded-sm border border-[color:var(--color-border)] px-1.5 py-0.5 typo-label text-[color:var(--color-text-lo)]"
          title="Status not tracked on territories yet"
        >
          {EM_DASH}
        </span>
        <span className="text-[color:var(--color-text-lo)]">·</span>
        <span>Code {EM_DASH}</span>
        {region && (
          <>
            <span className="text-[color:var(--color-text-lo)]">·</span>
            <span>{region}</span>
          </>
        )}
        {territory?.created_at != null && (
          <>
            <span className="text-[color:var(--color-text-lo)]">·</span>
            <span>Since {formatDate(territory.created_at as string)}</span>
          </>
        )}
        <span className="text-[color:var(--color-text-lo)]">·</span>
        <span>Owner {EM_DASH}</span>
      </div>

      <div className="mt-5">
        <KPIRow
          cards={[
            {
              label: "Locations",
              value: String(locationsTotal),
            },
            {
              label: "Active Jobs",
              value: String(activeJobsTotal),
            },
            {
              label: "Assigned Crews",
              value: String(crewsTotal),
            },
          ]}
        />
      </div>

      {/* Hidden testids on the KPI tiles let the page-level tests verify
          the numbers came from the right hook without coupling to KPIRow
          internals. Renders a 0-byte span — visible KPI value is in KPIRow. */}
      <div className="sr-only">
        <span data-testid="kpi-Locations">{locationsTotal}</span>
        <span data-testid="kpi-Active Jobs">{activeJobsTotal}</span>
        <span data-testid="kpi-Assigned Crews">{crewsTotal}</span>
      </div>

      <Tabs value={tab} onValueChange={setTab} className="flex flex-col mt-6">
        <TabsList className="h-auto bg-transparent p-0 border-b border-[color:var(--color-border)] rounded-none w-full justify-start gap-0 mb-0">
          <TabsTrigger
            value="overview"
            className="rounded-none border-b-2 border-transparent px-4 py-2 text-sm font-medium text-[color:var(--color-text-md)] shadow-none data-[state=active]:border-[color:var(--color-accent)] data-[state=active]:text-[color:var(--color-text-hi)] data-[state=active]:shadow-none data-[state=active]:bg-transparent"
          >
            Overview
          </TabsTrigger>
          <TabsTrigger
            value="locations"
            className="rounded-none border-b-2 border-transparent px-4 py-2 text-sm font-medium text-[color:var(--color-text-md)] shadow-none data-[state=active]:border-[color:var(--color-accent)] data-[state=active]:text-[color:var(--color-text-hi)] data-[state=active]:shadow-none data-[state=active]:bg-transparent"
          >
            Locations
          </TabsTrigger>
          <TabsTrigger
            value="crews"
            className="rounded-none border-b-2 border-transparent px-4 py-2 text-sm font-medium text-[color:var(--color-text-md)] shadow-none data-[state=active]:border-[color:var(--color-accent)] data-[state=active]:text-[color:var(--color-text-hi)] data-[state=active]:shadow-none data-[state=active]:bg-transparent"
          >
            Crews
          </TabsTrigger>
        </TabsList>

        <div className="flex gap-6 pt-6">
          {/* Main column */}
          <div className="flex-1 min-w-0 space-y-6">
            <TabsContent value="overview" className="mt-0 space-y-6">
              <LocationsSection
                rows={locations.slice(0, 5)}
                total={locationsTotal}
                isLoading={locationsQuery.isLoading}
              />
              <CrewsSection
                rows={crews.slice(0, 5)}
                total={crewsTotal}
                isLoading={crewsQuery.isLoading}
              />
              <section aria-labelledby="activity-section-heading">
                <h2
                  id="activity-section-heading"
                  className="typo-heading-2 text-[color:var(--color-text-hi)] mb-2"
                >
                  Activity
                </h2>
                <EntityActivityFeed
                  entityType="territory"
                  entityId={territoryId}
                  emptyTitle="No activity yet"
                  emptyDescription="Notes, calls, and messages for this territory will appear here."
                />
              </section>
            </TabsContent>

            <TabsContent value="locations" className="mt-0">
              <LocationsSection
                rows={locations}
                total={locationsTotal}
                isLoading={locationsQuery.isLoading}
              />
            </TabsContent>

            <TabsContent value="crews" className="mt-0">
              <CrewsSection
                rows={crews}
                total={crewsTotal}
                isLoading={crewsQuery.isLoading}
              />
            </TabsContent>
          </div>

          {/* Right rail — Pattern C reduced layout: Properties, Coverage,
              Activity preview. */}
          <aside
            className="w-[300px] flex-none border-l border-[color:var(--color-border)] pl-6 space-y-5"
            data-testid="territory-rail"
          >
            <RailSection title="Properties">
              <InfoRow label="Status" value={null} testId="prop-Status" />
              <InfoRow label="Code" value={null} testId="prop-Code" />
              <InfoRow label="Region" value={region} testId="prop-Region" />
              <InfoRow label="Bounds" value={null} testId="prop-Bounds" />
              <InfoRow
                label="Created"
                value={
                  territory?.created_at
                    ? formatDate(territory.created_at as string)
                    : null
                }
                testId="prop-Created"
              />
              <InfoRow label="Owner" value={null} testId="prop-Owner" />
            </RailSection>

            <RailSection title="Coverage">
              <InfoRow
                label="Locations"
                value={String(locationsTotal)}
                testId="coverage-Locations"
              />
              <InfoRow
                label="Active Jobs"
                value={String(activeJobsTotal)}
                testId="coverage-Active Jobs"
              />
              <InfoRow
                label="Crews"
                value={String(crewsTotal)}
                testId="coverage-Crews"
              />
            </RailSection>

            <RailSection title="Activity">
              <EntityActivityPreview
                entityType="territory"
                entityId={territoryId}
                rowCount={4}
                onSeeAll={() => setTab("overview")}
              />
            </RailSection>
          </aside>
        </div>
      </Tabs>
    </DetailPage>
  );
}
